import React, { useState, useEffect, useRef, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import TextInput from "../../components/TextInput/TextInput";
import Button from "../../components/Button/Button";
import LeftPanel from "../../components/LeftPanel/LeftPanel";
import OnboardingStepHeader from "../../components/OnboardingStepHeader/OnboardingStepHeader";
import { saveStep3Data, goToNextStep, goToPreviousStep, setStep3Data } from "../../redux/slices/OfficeOnboardingSlice";
import { uploadFile, clearUploadedFiles, getFile } from "../../redux/slices/FileSlice";
import { inviteOfficeUser, resetInviteUserApiSuccess } from "../../redux/slices/OfficeSlice";
import { getJobTitles } from "../../redux/slices/UserSlice";
import { emailExists } from "../../redux/slices/CurrentUserSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { FileTypes, ToastVariants } from "../../models";
import { logger } from "../../utils/logger";
import { AuthPermission } from "../../services/auth.service";
import { UserType } from "../../services/user.service";
import { OfficeService } from "../../services/office.service";
import { UserService } from "../../services/user.service";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { User } from "../../models/user.model";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { CheckIcon, PersonAddIcon } from "../../assets/icons/onboarding";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

const log = logger.getLogger("OnboardingStep3");

type ViewState = "selection" | "yesForm" | "noForm" | "invitationSuccess" | "alreadyInvited";

const Step3: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const { step3Data, isLoading } = useSelector((state: RootState) => state.officeOnboarding);
  const { loading: fileUploading } = useSelector((state: RootState) => state.file);
  const { inviteUserApiSuccess, loading: officeLoading, error: officeError, inviteErrorMessage } = useSelector((state: RootState) => state.office);
  const { jobTitles } = useSelector((state: RootState) => state.user);
  const { currentUser } = useSelector((state: RootState) => state.currentUser);

  // Get office ID and user ID from current user
  const officeId = currentUser?.offices?.[0]?.officeId || "";
  const userId = currentUser?.userId || "";
  const { currentOffice } = useSelector((state: RootState) => state.office);

  // View state to manage sub-screens
  const [currentView, setCurrentView] = useState<ViewState>("selection");
  const [isLoadingExistingData, setIsLoadingExistingData] = useState(true);
  const [invitedUser, setInvitedUser] = useState<{ firstName: string; lastName: string; email?: string } | null>(null);
  const [hasExistingData, setHasExistingData] = useState(false); // Track if we loaded existing data

  const [isPrimaryProvider, setIsPrimaryProvider] = useState<boolean | null>(step3Data?.isPrimaryProvider ?? null);
  const [physicianFirstName, setPhysicianFirstName] = useState(step3Data?.physicianFirstName || "");
  const [physicianLastName, setPhysicianLastName] = useState(step3Data?.physicianLastName || "");
  const [physicianEmail, setPhysicianEmail] = useState(step3Data?.physicianEmail || "");
  const [mpn, setMpn] = useState(step3Data?.medicalPractitionerNumber || "");
  const [signatureFileId, setSignatureFileId] = useState<string | null>(step3Data?.signatureFileId || null);
  const [signatureFileName, setSignatureFileName] = useState<string | null>(null);
  const [signatureImageUrl, setSignatureImageUrl] = useState<string | null>(null);
  const { getFileDetails } = useSelector((state: RootState) => state.file);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [invitationSent, setInvitationSent] = useState(step3Data?.invitationSent || false);
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  // Fetch job titles on mount
  useEffect(() => {
    if (jobTitles.length === 0) {
      dispatch(getJobTitles());
    }
  }, [dispatch, jobTitles.length]);

  // Check for existing data on mount (only if step3Data doesn't exist)
  useEffect(() => {
    const checkExistingData = async () => {
      // If we already have step3Data, don't check API
      if (step3Data) {
        setIsLoadingExistingData(false);
        return;
      }

      if (!officeId || !userId) {
        setIsLoadingExistingData(false);
        return;
      }

      try {
        const currentUserData = await UserService.getUser(userId);
        if (currentUserData?.mpcNumber && currentUserData?.signImageFileId) {
          // User already has MPN and signature - pre-populate and go to yesForm
          setMpn(currentUserData.mpcNumber);
          setSignatureFileId(currentUserData.signImageFileId);
          setIsPrimaryProvider(true);
          setCurrentView("yesForm");
          setHasExistingData(true); // Mark that we have existing data
          // Fetch signature image URL
          if (currentUserData.signImageFileId) {
            try {
              const fileResult = await dispatch(getFile({ fileId: currentUserData.signImageFileId })).unwrap();
              if (fileResult?.url) {
                setSignatureImageUrl(fileResult.url);
                setSignatureFileName(fileResult.originalFilename || "Signature file");
              }
            } catch (error) {
              log.warn(`Failed to fetch signature image: ${error instanceof Error ? error.message : String(error)}`);
            }
          }
          setIsLoadingExistingData(false);
          return;
        }

        // Check if someone was already invited (from Office V2 API)
        const officeV2Data = await OfficeService.getOfficeV2(officeId);
        if (officeV2Data?.primaryCareProviders && officeV2Data.primaryCareProviders.length > 0) {
          const invitedPCP = officeV2Data.primaryCareProviders.find(pcp => pcp.userState === "invited" || pcp.userState === "Invited");

          if (invitedPCP) {
            setInvitedUser({
              firstName: invitedPCP.firstName || "",
              lastName: invitedPCP.lastName || "",
              email: (invitedPCP as any).email,
            });
            setIsPrimaryProvider(false);
            setInvitationSent(true);
            setCurrentView("noForm");
            setHasExistingData(true);
            setIsLoadingExistingData(false);
            return;
          }
        }

        setIsLoadingExistingData(false);
      } catch (error) {
        log.warn(`Failed to check existing data: ${error instanceof Error ? error.message : String(error)}`);
        setIsLoadingExistingData(false);
      }
    };

    checkExistingData();
  }, [officeId, userId, dispatch, step3Data]);

  // Fetch signature image URL when signatureFileId changes
  useEffect(() => {
    const fetchSignatureImage = async () => {
      if (signatureFileId && !signatureImageUrl) {
        try {
          const fileResult = await dispatch(getFile({ fileId: signatureFileId })).unwrap();
          if (fileResult?.url) {
            setSignatureImageUrl(fileResult.url);
            if (fileResult.originalFilename) {
              setSignatureFileName(fileResult.originalFilename);
            }
          }
        } catch (error) {
          log.warn(`Failed to fetch signature image: ${error instanceof Error ? error.message : String(error)}`);
        }
      }
    };

    fetchSignatureImage();
  }, [signatureFileId, signatureImageUrl, dispatch]);

  useEffect(() => {
    if (step3Data) {
      setIsPrimaryProvider(step3Data.isPrimaryProvider);
      setPhysicianFirstName(step3Data.physicianFirstName || "");
      setPhysicianLastName(step3Data.physicianLastName || "");
      setPhysicianEmail(step3Data.physicianEmail || "");
      setMpn(step3Data.medicalPractitionerNumber || "");
      setSignatureFileId(step3Data.signatureFileId || null);
      setInvitationSent(step3Data.invitationSent || false);

      // If we have existing data, go to the appropriate view
      if (step3Data.isPrimaryProvider === true) {
        setCurrentView("yesForm");
      } else if (step3Data.isPrimaryProvider === false) {
        if (step3Data.invitationSent) {
          setCurrentView("invitationSuccess");
        } else {
          setCurrentView("noForm");
        }
      }
    }
  }, [step3Data]);

  // Handle successful invitation
  useEffect(() => {
    if (inviteUserApiSuccess && isSendingInvitation) {
      setInvitationSent(true);
      setIsSendingInvitation(false);
      setCurrentView("invitationSuccess");
      dispatch(resetInviteUserApiSuccess());
    }
  }, [inviteUserApiSuccess, isSendingInvitation, dispatch]);

  // Handle invitation error from state
  useEffect(() => {
    if (officeError && isSendingInvitation) {
      setIsSendingInvitation(false);
      // Use the actual error message from API if available
      const errorMessage = inviteErrorMessage || "Failed to send invitation. Please try again.";
      toast?.openToast(errorMessage, 3000, ToastVariants.Error);
    }
  }, [officeError, inviteErrorMessage, isSendingInvitation, toast]);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case "mpn":
        const trimmedMpn = value?.trim() ?? "";

        // Required validation
        if (!trimmedMpn) {
          return "MPN, CPSO number or MINC is required.";
        }

        // Allow only alphanumeric characters
        if (!/^[a-zA-Z0-9]+$/.test(trimmedMpn)) {
          return "MPN can only contain letters and numbers.";
        }

        // Max length 12
        if (trimmedMpn.length > 12) {
          return "MPN cannot be longer than 12 characters.";
        }

        return "";
      case "physicianFirstName":
        if (!isPrimaryProvider) {
          if (!value || !value.trim()) {
            return "First name is required.";
          }
          const trimmedValue = value.trim();
          if (trimmedValue.length < 2) {
            return "First name must be at least 2 characters.";
          }
          if (trimmedValue.length > 50) {
            return "First name must be at most 50 characters.";
          }
          // Allow alphabetic characters, spaces, and common special characters (apostrophes, hyphens, periods)
          if (!/^[a-zA-Z\s'.-]+$/.test(trimmedValue)) {
            return "First name must contain only letters.";
          }
        }
        return "";
      case "physicianLastName":
        if (!isPrimaryProvider) {
          if (!value || !value.trim()) {
            return "Last name is required.";
          }
          const trimmedValue = value.trim();
          if (trimmedValue.length < 2) {
            return "Last name must be at least 2 characters.";
          }
          if (trimmedValue.length > 50) {
            return "Last name must be at most 50 characters.";
          }
          // Allow alphabetic characters, spaces, and common special characters (apostrophes, hyphens, periods)
          if (!/^[a-zA-Z\s'.-]+$/.test(trimmedValue)) {
            return "Last name must contain only letters.";
          }
        }
        return "";
      case "physicianEmail":
        if (!isPrimaryProvider) {
          if (!value || !value.trim()) {
            return "Email is required.";
          }
          const trimmedValue = value.trim();
          // Check for spaces in email
          if (/\s/.test(trimmedValue)) {
            return "Please enter a valid email address.";
          }
          // Validate email format
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) {
            return "Please enter a valid email address.";
          }
        }
        return "";
      default:
        return "";
    }
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    switch (field) {
      case "physicianFirstName":
        setPhysicianFirstName(value);
        break;
      case "physicianLastName":
        setPhysicianLastName(value);
        break;
      case "physicianEmail":
        setPhysicianEmail(value);
        break;
      case "mpn":
        // Only allow digits and hyphens
        const filteredValue = value.replace(/[^a-zA-Z0-9]/g, "");
        // Limit to 10 characters max (including hyphen)
        const limitedValue = filteredValue.length > 12 ? filteredValue.slice(0, 12) : filteredValue;
        setMpn(limitedValue);
        break;
    }
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleInputBlur = (field: string) => () => {
    const value =
      field === "physicianFirstName"
        ? physicianFirstName
        : field === "physicianLastName"
        ? physicianLastName
        : field === "physicianEmail"
        ? physicianEmail
        : mpn;
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleProviderSelection = (isPrimary: boolean) => {
    setIsPrimaryProvider(isPrimary);
    setErrors({});
    if (isPrimary) {
      // Clear physician fields if selecting "Yes"
      setPhysicianFirstName("");
      setPhysicianLastName("");
      setPhysicianEmail("");
    }
    // Don't change view immediately - wait for "Save & Continue" button
  };

  const handleContinueToForm = () => {
    if (isPrimaryProvider === null) {
      toast?.openToast("Please select whether you are the primary care provider", 3000, ToastVariants.Error);
      return;
    }
    if (isPrimaryProvider) {
      setCurrentView("yesForm");
    } else {
      setCurrentView("noForm");
    }
  };
  const handleBackToSelection = () => {
    // From invitation success, go directly to step 2
    if (currentView === "invitationSuccess" || "yesForm") {
      dispatch(goToPreviousStep());
      return;
    }
    // If we have existing data, go to previous step instead of selection
    if (hasExistingData) {
      dispatch(goToPreviousStep());
      return;
    }
    setCurrentView("selection");
    setIsPrimaryProvider(null);
    setErrors({});
  };

  // File upload handling
  const handleSignatureFile = async (file: File) => {
    // Validate file type - PNG or JPG/JPEG only
    // Note: Both .jpg and .jpeg files have MIME type "image/jpeg" (standard)
    if (file.type !== FileTypes.PNG && file.type !== FileTypes.JPEG) {
      setErrors(prev => ({ ...prev, signature: "Signature must be a PNG or JPG file." }));
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5.1 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, signature: "Attachments cannot be larger than 5MB." }));
      return;
    }

    // Clear previous errors
    setErrors(prev => ({ ...prev, signature: "" }));

    try {
      // Upload to AWS
      const result = await dispatch(uploadFile({ file })).unwrap();
      if (result?.fileId) {
        setSignatureFileId(result.fileId);
        setSignatureFileName(file.name);
      }
    } catch (err) {
      toast?.openToast("Failed to upload signature. Please try again.", 3000, ToastVariants.Error);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    await handleSignatureFile(file);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    await handleSignatureFile(file);

    // Reset input to allow re-uploading the same file via click if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleChangeSignature = () => {
    fileInputRef.current?.click();
  };

  const handleInvitePhysician = async () => {
    const firstNameError = validateField("physicianFirstName", physicianFirstName);
    const lastNameError = validateField("physicianLastName", physicianLastName);
    const emailError = validateField("physicianEmail", physicianEmail);

    if (firstNameError || lastNameError || emailError) {
      setErrors({
        physicianFirstName: firstNameError,
        physicianLastName: lastNameError,
        physicianEmail: emailError,
      });
      return;
    }

    // Check email uniqueness
    if (physicianEmail.trim()) {
      try {
        const emailExistResult = await dispatch(emailExists({ email: physicianEmail.trim() }));
        if (emailExistResult.payload === true) {
          setErrors({
            physicianEmail: "Email already exits",
          });
          return;
        }
      } catch (error) {
        // If email check fails, continue with invitation (API will handle it)
        log.warn(`Email existence check failed: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    if (!officeId) {
      toast?.openToast("Office ID not found. Please try again.", 3000, ToastVariants.Error);
      return;
    }

    // Find Physician job title
    const physicianJobTitle = jobTitles.find(jt => jt.name.toLowerCase() === "physician" || jt.name.toLowerCase().includes("physician"));

    if (!physicianJobTitle) {
      toast?.openToast("Physician job title not found. Please contact support.", 3000, ToastVariants.Error);
      return;
    }

    setIsSendingInvitation(true);
    setErrors({});

    try {
      // Permissions for Primary Care Provider - Signing permission is key for PCP
      const permissions: AuthPermission[] = [AuthPermission.Signing];

      await dispatch(
        inviteOfficeUser({
          firstName: physicianFirstName,
          lastName: physicianLastName,
          email: physicianEmail,
          selectedPermissions: permissions,
          primaryCareProvider: true,
          officeId: officeId,
          userType: UserType.User,
          jobTitleId: physicianJobTitle.jobTitleId,
        })
      ).unwrap();
    } catch {
      // Error handling is done via useEffect watching officeError state
    }
  };

  const handleBack = () => {
    dispatch(goToPreviousStep());
  };

  const handleSubmit = async () => {
    const newErrors: { [key: string]: string } = {};
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    if (isPrimaryProvider === null) {
      toast?.openToast("Please select whether you are the primary care provider", 3000, ToastVariants.Error);
      return;
    }

    if (isPrimaryProvider) {
      const mpnError = validateField("mpn", mpn);
      if (mpnError) {
        newErrors.mpn = mpnError;
      }
      if (!signatureFileId) {
        newErrors.signature = "Signature is required.";
      }
    } else {
      // If someone was already invited (invitedUser exists), allow proceeding
      if (!invitationSent && !invitedUser) {
        toast?.openToast("Please send invitation to the physician first", 3000, ToastVariants.Error);
        return;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsSubmitting(true);

    try {
      // If user is the primary provider, save MPN and signature to API
      if (isPrimaryProvider && signatureFileId) {
        const result = await OfficeService.saveMpinSignature(officeId, userId, mpn, signatureFileId);
        if (!result.success) {
          toast?.openToast(result.errorMessage || "Failed to save MPN and signature", 3000, ToastVariants.Error);
          return;
        }
      }

      const step3Payload = {
        isPrimaryProvider: isPrimaryProvider!,
        medicalPractitionerNumber: isPrimaryProvider ? mpn : undefined,
        physicianFirstName: !isPrimaryProvider && invitedUser ? invitedUser.firstName : !isPrimaryProvider ? physicianFirstName : undefined,
        physicianLastName: !isPrimaryProvider && invitedUser ? invitedUser.lastName : !isPrimaryProvider ? physicianLastName : undefined,
        physicianEmail: !isPrimaryProvider && invitedUser ? invitedUser.email : !isPrimaryProvider ? physicianEmail : undefined,
        signatureFileId: signatureFileId || undefined,
        invitationSent: !isPrimaryProvider ? (invitedUser ? true : invitationSent) : undefined,
      };

      await dispatch(saveStep3Data({ data: step3Payload, officeId: officeId })).unwrap();
      dispatch(setStep3Data(step3Payload));
      dispatch(clearUploadedFiles());
      dispatch(goToNextStep());
    } catch (err) {
      toast?.openToast("Failed to save. Please try again.", 3000, ToastVariants.Error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVideoPlay = () => {
    log.debug("Video play clicked");
  };

  // Render Selection View
  const renderSelectionView = () => (
    <>
      {/* Title and Description */}
      <div className="mb-6">
        <h1 className="text-xl leading-normal tracking-[-0.45px] font-medium mb-2">Assign a Primary Care Provider</h1>
        <p className="text-gray-500 mb-8 tracking-[-0.31px]">Your office needs a Primary Care Provider to publish and start accepting requests.</p>
        <p className="tracking-[-0.31px] font-inter">Are you the Primary Care Provider of this Office?</p>
        <p className="text-[14px] text-[#6b7280]">Note: You can add more staff, including other Primary Care Providers later as needed.</p>
      </div>

      {/* Provider Selection Cards */}
      <div className="mb-8 flex gap-4">
        <button
          onClick={() => handleProviderSelection(true)}
          className={`flex-1 flex flex-col items-center justify-center py-8 px-6 rounded-lg border transition-all ${
            isPrimaryProvider === true ? "bg-[#FFF5F2] border-docnote-orange" : "bg-white border-black-10 hover:border-gray-300"
          }`}
        >
          <CheckIcon width={32} height={32} className={`mb-3 ${isPrimaryProvider === true ? "text-docnote-orange" : "text-gray-400"}`} />
          <span className={`text-base font-medium ${isPrimaryProvider === true ? "text-docnote-orange" : "text-gray-700"}`}>Yes, that&apos;s me</span>
        </button>
        <button
          onClick={() => handleProviderSelection(false)}
          className={`flex-1 flex flex-col items-center justify-center py-8 px-6 rounded-lg border transition-all ${
            isPrimaryProvider === false ? "bg-[#FFF5F2] border-docnote-orange" : "bg-white border-black-10 hover:border-gray-300"
          }`}
        >
          <PersonAddIcon width={32} height={32} className={`mb-3 ${isPrimaryProvider === false ? "text-docnote-orange" : "text-gray-400"}`} />
          <span className={`text-base font-medium ${isPrimaryProvider === false ? "text-docnote-orange" : "text-gray-700"}`}>
            No, that&apos;s not me
          </span>
        </button>
      </div>

      {errors.general && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">{errors.general}</div>}
    </>
  );

  // Render Yes Form (Primary Care Provider Form)
  const renderYesForm = () => (
    <>
      {/* Title */}
      <div className="mb-6">
        <h1 className="text-xl leading-normal tracking-[-0.45px] font-medium mb-2">Complete Your Medical Practioner Profile</h1>
        <p className="text-gray-500 tracking-[-0.31px]">As the primary care provider, we need a few more details to set up your profile.</p>
      </div>

      {errors.general && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">{errors.general}</div>}

      {/* Form Fields */}
      <div className="space-y-6">
        {/* MPN Input */}
        <TextInput
          label="Medical Practitioner Number (MPN, CPSO number, MINC, etc.)"
          placeholder="Enter your MPN, CPSO number, MINC, etc."
          value={mpn}
          onChangeFunc={handleInputChange("mpn")}
          onBlur={handleInputBlur("mpn")}
          isRequired={true}
          isError={!!errors.mpn}
          errorMsg={errors.mpn}
          name="mpn"
          maxLength={12}
          extraInputClass="bg-gray-100 !border-0"
        />

        {/* Signature Upload */}
        <div>
          <label className="text-sm font-medium block mb-1.5">
            Your Signature<span className="text-red-500">*</span>
          </label>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".png,image/png,.jpg,.jpeg,image/jpeg,image/jpg"
            onChange={handleFileSelect}
            className="hidden"
          />

          {signatureFileId ? (
            // Success state - green outline with file info and image preview
            <div className="border border-green-500 bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <CheckCircleOutlineIcon className="text-green-500" sx={{ fontSize: 24 }} />
                  <div>
                    <p className="text-green-600 font-medium">Signature uploaded successfully</p>
                    <p className="text-gray-600 text-sm">{signatureFileName || "Signature file"}</p>
                  </div>
                </div>
                <button onClick={handleChangeSignature} className="text-docnote-orange font-medium hover:underline" disabled={fileUploading}>
                  Change
                </button>
              </div>
              {/* Display signature image if available */}
              {signatureImageUrl && (
                <div className="mt-3 border border-gray-200 rounded p-2 bg-white">
                  <img src={signatureImageUrl} alt="Signature" className="max-h-32 mx-auto" />
                </div>
              )}
            </div>
          ) : (
            // Upload area
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all hover:border-docnote-orange hover:bg-orange-50 ${
                errors.signature ? "border-red-500 bg-red-50" : isDragActive ? "border-docnote-orange bg-orange-50" : "border-gray-300 bg-gray-50"
              }`}
            >
              {fileUploading ? (
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-docnote-orange mb-3"></div>
                  <p className="text-gray-600">Uploading...</p>
                </div>
              ) : (
                <>
                  <CloudUploadOutlinedIcon className="text-gray-400 mb-2" sx={{ fontSize: 40 }} />
                  <p className="text-gray-600">Click or drag &amp; drop signature – PNG or JPG (max 5MB)</p>
                </>
              )}
            </div>
          )}
          {errors.signature && <p className="text-sm text-red-500 mt-2">{errors.signature}</p>}
        </div>
      </div>
      <div className="text-[14px] text-[#6b7280] mt-1">
        Note: Your signature will only be used to autogenerate Provider Notes assigned to you. We will not share or use this signature without your
        permission.
      </div>
    </>
  );

  // Render No Form (Invite Physician Form)
  const renderNoForm = () => {
    // If someone was already invited, show message instead of form
    if (invitedUser) {
      return (
        <>
          {/* Title */}
          <div className="mb-6">
            <h1 className="text-xl leading-normal tracking-[-0.45px] font-medium mb-2">Primary Care Provider Already Invited</h1>
            <p className="text-gray-500 tracking-[-0.31px]">A primary care provider has already been invited for this office.</p>
          </div>

          {/* Already Invited Message */}
          <div className="border-2 border-blue-400 bg-blue-50 rounded-lg p-8">
            <div className="flex items-center gap-3 mb-4">
              <TaskAltIcon className="text-blue-500" sx={{ fontSize: 32 }} />
              <div>
                <p className="text-blue-600 font-medium text-lg">
                  {invitedUser.firstName} {invitedUser.lastName} has already been invited
                </p>
                {invitedUser.email && <p className="text-gray-600 text-sm mt-1">Email: {invitedUser.email}</p>}
              </div>
            </div>
            <p className="text-gray-700">
              The invitation has been sent. They will complete their profile and add their credentials when they accept the invitation.
            </p>
          </div>
        </>
      );
    }

    return (
      <>
        {/* Title */}
        <div className="mb-6">
          <h1 className="text-xl leading-normal tracking-[-0.45px] font-medium mb-2">Invite Primary Care Provider</h1>
          <p className="text-gray-500 tracking-[-0.31px]">
            We&apos;ll send them a secure invitation to complete their profile and add their credentials.
          </p>
        </div>

        {errors.general && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">{errors.general}</div>}

        <div className="space-y-6">
          {/* First Name & Last Name in one row */}
          <div className="flex gap-4">
            <div className="flex-1">
              <TextInput
                label="First Name"
                placeholder=""
                value={physicianFirstName}
                onChangeFunc={handleInputChange("physicianFirstName")}
                onBlur={handleInputBlur("physicianFirstName")}
                isRequired={true}
                isError={!!errors.physicianFirstName}
                errorMsg={errors.physicianFirstName}
                name="physicianFirstName"
                extraInputClass="bg-gray-100 !border-0"
              />
            </div>
            <div className="flex-1">
              <TextInput
                label="Last Name"
                placeholder=""
                value={physicianLastName}
                onChangeFunc={handleInputChange("physicianLastName")}
                onBlur={handleInputBlur("physicianLastName")}
                isRequired={true}
                isError={!!errors.physicianLastName}
                errorMsg={errors.physicianLastName}
                name="physicianLastName"
                extraInputClass="bg-gray-100 !border-0"
              />
            </div>
          </div>

          {/* Email Address */}
          <div>
            <TextInput
              label="Email Address"
              placeholder=""
              value={physicianEmail}
              onChangeFunc={handleInputChange("physicianEmail")}
              onBlur={handleInputBlur("physicianEmail")}
              isRequired={true}
              isError={!!errors.physicianEmail}
              errorMsg={errors.physicianEmail}
              name="physicianEmail"
              extraInputClass="bg-gray-100 !border-0"
            />
          </div>

          {errors.invitation && <p className="text-sm text-red-500">{errors.invitation}</p>}
        </div>
      </>
    );
  };

  // Render Invitation Success View
  const renderInvitationSuccess = () => (
    <>
      {/* Title */}
      <div className="mb-8">
        <h1 className="text-xl leading-normal tracking-[-0.45px] font-medium">Invitation Sent</h1>
      </div>

      {/* Success Card */}
      <div className="border-2 border-green-400 bg-green-50 rounded-lg p-8">
        {/* Checkmark Icon */}
        <div className="flex justify-center mb-4">
          <TaskAltIcon className="text-green-500" sx={{ fontSize: 64 }} />
        </div>

        {/* Success Message */}
        <p className="text-green-600 text-center font-medium mb-6">Invitation sent successfully!</p>

        {/* Physician Details */}
        <div className="bg-white rounded-lg p-6 space-y-3">
          <div>
            <p className="text-gray-500 text-sm">Primary Care Provider Name</p>
            <p className="font-medium">
              {physicianFirstName} {physicianLastName}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Email</p>
            <p className="font-medium">{physicianEmail}</p>
          </div>
        </div>
      </div>
    </>
  );
  return (
    <div className="flex h-full min-h-0">
      {/* Left Panel */}
      <LeftPanel onVideoPlay={handleVideoPlay} />

      {/* Right Panel */}
      <div className="w-full md:w-2/3 bg-white flex flex-col h-full min-h-0">
        {/* Sticky Header with Progress */}
        <div className="sticky top-0 z-10 bg-white flex-shrink-0">
          <OnboardingStepHeader currentStep={3} totalSteps={5} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="flex justify-center">
            <div className="p-8 w-full max-w-4xl">
              {isLoadingExistingData ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-docnote-orange"></div>
                </div>
              ) : (
                <>
                  {currentView === "selection" && renderSelectionView()}
                  {currentView === "yesForm" && renderYesForm()}
                  {currentView === "noForm" && renderNoForm()}
                  {currentView === "invitationSuccess" && renderInvitationSuccess()}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Sticky Footer - Only show for views that have navigation buttons */}
        {(currentView === "selection" || currentView === "yesForm" || currentView === "noForm" || currentView === "invitationSuccess") && (
          <div className="sticky bottom-0 z-10 bg-white flex-shrink-0 p-8">
            <div className="flex justify-between">
              {currentView === "selection" && (
                <>
                  <Button text="Back" onClick={handleBack} variant="Tertiary" width="fit" className="px-8" buttonStyle="square" />
                  {isPrimaryProvider !== null && (
                    <Button
                      text="Save & Continue"
                      onClick={handleContinueToForm}
                      variant="Primary"
                      width="fit"
                      className="px-8"
                      buttonStyle="square"
                    />
                  )}
                </>
              )}
              {currentView === "yesForm" && (
                <>
                  <Button
                    text="Back"
                    onClick={hasExistingData ? handleBack : handleBackToSelection}
                    variant="Tertiary"
                    width="fit"
                    className="px-8"
                    buttonStyle="square"
                  />
                  <Button
                    text="Save & Continue"
                    onClick={handleSubmit}
                    disabled={isLoading || fileUploading || isSubmitting}
                    variant={isLoading || isSubmitting ? "loading" : "Primary"}
                    width="fit"
                    className="px-8"
                    buttonStyle="square"
                  />
                </>
              )}
              {currentView === "noForm" && !invitedUser && (
                <>
                  <Button text="Back" onClick={handleBackToSelection} variant="Tertiary" width="fit" className="px-8" buttonStyle="square" />
                  <Button
                    text="Send Invitation"
                    onClick={handleInvitePhysician}
                    disabled={isSendingInvitation || officeLoading}
                    variant={isSendingInvitation || officeLoading ? "loading" : "Primary"}
                    width="fit"
                    className="px-8"
                    buttonStyle="square"
                  />
                </>
              )}
              {currentView === "noForm" && invitedUser && (
                <>
                  <Button text="Back" onClick={handleBack} variant="Tertiary" width="fit" className="px-8" buttonStyle="square" />
                  <Button
                    text="Continue Setup"
                    onClick={handleSubmit}
                    disabled={isLoading || isSubmitting}
                    variant={isLoading || isSubmitting ? "loading" : "Primary"}
                    width="fit"
                    className="px-8"
                    buttonStyle="square"
                  />
                </>
              )}
              {currentView === "invitationSuccess" && (
                <>
                  <Button text="Back" onClick={handleBackToSelection} variant="Tertiary" width="fit" className="px-8" buttonStyle="square" />
                  <Button
                    text="Continue Setup"
                    onClick={handleSubmit}
                    disabled={isLoading || isSubmitting}
                    variant={isLoading || isSubmitting ? "loading" : "Primary"}
                    width="fit"
                    className="px-8"
                    buttonStyle="square"
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Step3;
